/**
 * Financial calculation utilities for precise currency and rate operations
 * Used for invoice generation, financial reporting, and any monetary calculations
 */
import { FINANCE_ROUNDING_EFFECTIVE_RATE_DECIMAL_PLACES } from '@/modules/finance/common/constants/rounding.constants';

/**
 * Parses form input values to numbers, handling edge cases
 * @param value - Input value from form (can be string, number, null, undefined)
 * @returns Parsed number or 0 if invalid
 */
export const parseFormNumber = (value: unknown): number => {
  if (value === null || value === undefined || value === '') return 0;
  const parsed = typeof value === 'string' ? parseFloat(value) : Number(value);
  return isNaN(parsed) ? 0 : parsed;
};

/** Formats a finance amount with its ISO currency code. */
export function formatAmountWithCurrencyCode(amount: string, currencyCode: string): string {
  return `${amount} ${currencyCode}`;
}

/**
 * Rounds number to specified decimal precision
 * @param num - Number to round
 * @param decimals - Number of decimal places
 * @returns Rounded number
 */
export const roundToPrecision = (num: number, decimals: number): number => {
  const multiplier = Math.pow(10, decimals);
  return Math.round(num * multiplier) / multiplier;
};

/**
 * Converts currency amount to cents for precise arithmetic
 * Avoids floating-point precision issues in financial calculations
 * @param amount - Amount in currency units (e.g., dollars)
 * @returns Amount in cents (rounded to nearest integer)
 */
export const toCents = (amount: number): number => Math.round(amount * 100);

/**
 * Converts cents back to currency amount
 * @param cents - Amount in cents
 * @returns Amount in currency units
 */
export const fromCents = (cents: number): number => cents / 100;

/**
 * Determines the number of decimal places in a number
 * Handles both standard notation and scientific notation
 * @param num - Number to analyze
 * @returns Number of decimal places
 */
export const getDecimalPlaces = (num: number): number => {
  if (Math.floor(num) === num) return 0;
  const str = num.toString();
  const decimalPart = str.split('.')[1];
  return decimalPart ? decimalPart.length : 0;
};

/**
 * Calculates weighted average rate across multiple entries
 * Uses cent-based arithmetic to avoid floating-point precision issues
 * @param entries - Array of entries with hours and rate
 * @returns Weighted average rate with dynamic precision (2-4 decimals)
 */
export const calculateWeightedAverageRate = (
  entries: Array<{ hours: number; rate: number }>
): { averageRate: number; precision: number } => {
  // With a single entry the weighted average is exactly the entry's own rate —
  // avoid the cents-based rounding path which introduces floating-point error
  // (e.g. toCents(12.1231 * 12) / 12.1231 ≈ 12.0002 instead of 12).
  if (entries.length === 1) {
    const { rate } = entries[0];
    const precision = Math.max(
      0,
      Math.min(FINANCE_ROUNDING_EFFECTIVE_RATE_DECIMAL_PLACES, getDecimalPlaces(rate))
    );
    return { averageRate: rate, precision };
  }

  let totalAmount = 0;
  let totalHours = 0;

  entries.forEach(({ hours, rate }) => {
    totalHours += hours;
    totalAmount += hours * rate;
  });

  const avgRate = totalHours > 0 ? totalAmount / totalHours : 0;

  // Determine precision dynamically (max: 8) to match the user-editable
  // precision on the member/project-level rate inputs.
  const precision = Math.min(
    FINANCE_ROUNDING_EFFECTIVE_RATE_DECIMAL_PLACES,
    getDecimalPlaces(avgRate)
  );
  const averageRate = roundToPrecision(avgRate, precision);

  return { averageRate, precision };
};

/**
 * Formats a number showing only its significant decimal places.
 * Whole numbers show no decimal point; fractional numbers show exactly
 * as many decimal places as they actually have (up to maxDecimals).
 * Examples: 25 → "25", 25.1 → "25.1", 25.1234 → "25.1234"
 * @param num - Number to format
 * @param maxDecimals - Maximum decimal places to show (default: 4)
 * @returns Formatted string with trimmed trailing zeros
 */
export const formatTrimmedDecimal = (num: number, maxDecimals = 4): string => {
  const places = Math.min(getDecimalPlaces(num), maxDecimals);
  return num.toFixed(places);
};

/**
 * Sums total amount from multiple entries preserving up to 4 decimal places of precision.
 * Intentionally avoids cent-based (2-decimal) rounding so that invoice totals derived
 * from 4-decimal hours and rates are not truncated prematurely.
 * @param entries - Array of entries with hours and rate
 * @returns Total amount with up to 4 decimal precision
 */
export const calculateTotalAmount = (entries: Array<{ hours: number; rate: number }>): number => {
  const total = entries.reduce((sum, { hours, rate }) => sum + hours * rate, 0);
  return roundToPrecision(total, 4);
};
